import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function QualityControl() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/qc/${id}`, ["qc", "qc-analytics"]);
  const clearAll = useBulkDelete((r) => `/qc/${r.id}`, ["qc", "qc-analytics"]);
  const { data: records, isLoading } = useList("qc", "/qc");
  const { data: analytics } = useList("qc-analytics", "/qc/analytics");
  const { data: orders = [] } = useList("orders", "/orders");
  const { data: defects = [] } = useList("defect_types", "/defect_types");
  const { data: departments = [] } = useList("departments", "/departments");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ order_ref: "", inspected_quantity: "", passed_quantity: "", rejected_quantity: "", rework_quantity: "", defect_type: "", department_responsible: "", defect_description: "", inspector: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.order_ref) return toast.error("Select order");
    try {
      await api.post("/qc", { ...f, inspected_quantity: Number(f.inspected_quantity) || 0, passed_quantity: Number(f.passed_quantity) || 0, rejected_quantity: Number(f.rejected_quantity) || 0, rework_quantity: Number(f.rework_quantity) || 0 });
      toast.success("QC record saved"); qc.invalidateQueries(); setOpen(false);
      setF({ order_ref: "", inspected_quantity: "", passed_quantity: "", rejected_quantity: "", rework_quantity: "", defect_type: "", department_responsible: "", defect_description: "", inspector: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Quality Control" subtitle="Inspections, defects & rejection analytics" testid="qc-header">
        {canWrite("qc") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-qc-btn"><Plus className="w-4 h-4" /> New Inspection</Button>}
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-5"><h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Top Defects</h3>
          <div style={{ width: "100%", height: 220 }}>
            {analytics?.by_defect?.length ? (
              <ResponsiveContainer><BarChart data={analytics.by_defect} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} width={110} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(var(--chart-5))" radius={[0, 4, 4, 0]} />
              </BarChart></ResponsiveContainer>
            ) : <EmptyState title="No defect data" />}
          </div>
        </Card>
        <Card className="p-5"><h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Defects by Department</h3>
          <div style={{ width: "100%", height: 220 }}>
            {analytics?.by_department?.length ? (
              <ResponsiveContainer><BarChart data={analytics.by_department}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
              </BarChart></ResponsiveContainer>
            ) : <EmptyState title="No data" />}
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        {!records?.length ? <EmptyState title="No QC records" /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Order</TableHead><TableHead className="text-right">Inspected</TableHead>
              <TableHead className="text-right">Passed</TableHead><TableHead className="text-right">Rejected</TableHead>
              <TableHead className="text-right">Rej %</TableHead><TableHead>Defect</TableHead><TableHead>Dept</TableHead><TableHead>Inspector</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>{records.map((r) => (
              <TableRow key={r.id} data-testid={`qc-row-${r.id}`}>
                <TableCell className="text-sm">{r.date}</TableCell>
                <TableCell className="font-num text-primary font-semibold">{r.order_id}</TableCell>
                <TableCell className="text-right font-num">{fmt(r.inspected_quantity)}</TableCell>
                <TableCell className="text-right font-num text-success">{fmt(r.passed_quantity)}</TableCell>
                <TableCell className="text-right font-num text-destructive">{fmt(r.rejected_quantity)}</TableCell>
                <TableCell className="text-right font-num font-bold">{r.rejection_pct}%</TableCell>
                <TableCell className="text-sm">{r.defect_type}</TableCell>
                <TableCell className="text-sm">{r.department_responsible}</TableCell>
                <TableCell className="text-sm">{r.inspector}</TableCell>
                <TableCell>{canWrite("qc") && <Button size="icon" variant="ghost" onClick={() => delOne(r.id, "Delete this QC record?")} data-testid={`delete-qc-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="qc-form">
          <DialogHeader><DialogTitle>QC Inspection</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Order</Label>
              <Select value={f.order_ref} onValueChange={(v) => set("order_ref", v)}>
                <SelectTrigger data-testid="qc-order-select"><SelectValue placeholder="Order" /></SelectTrigger>
                <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_id} — {o.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Inspected</Label><Input type="number" value={f.inspected_quantity} onChange={(e) => set("inspected_quantity", e.target.value)} data-testid="qc-inspected-input" /></div>
            <div><Label>Passed</Label><Input type="number" value={f.passed_quantity} onChange={(e) => set("passed_quantity", e.target.value)} /></div>
            <div><Label>Rejected</Label><Input type="number" value={f.rejected_quantity} onChange={(e) => set("rejected_quantity", e.target.value)} data-testid="qc-rejected-input" /></div>
            <div><Label>Rework</Label><Input type="number" value={f.rework_quantity} onChange={(e) => set("rework_quantity", e.target.value)} /></div>
            <div><Label>Defect Type</Label>
              <Select value={f.defect_type} onValueChange={(v) => set("defect_type", v)}>
                <SelectTrigger><SelectValue placeholder="Defect" /></SelectTrigger>
                <SelectContent>{defects.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Dept Responsible</Label>
              <Select value={f.department_responsible} onValueChange={(v) => set("department_responsible", v)}>
                <SelectTrigger><SelectValue placeholder="Dept" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={f.defect_description} onChange={(e) => set("defect_description", e.target.value)} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="qc-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
